import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { create } from '@bufbuild/protobuf'
import { FieldMaskSchema } from '@bufbuild/protobuf/wkt'
import { useQuery } from '@connectrpc/connect-query'
import { Check, ChevronDown, Circle, Eye, Loader2, MoreHorizontal, Send } from 'lucide-react'
import { toast } from 'sonner'
import { formatDistanceToNow } from 'date-fns'

import { getQuoteDetail } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  DuplicateQuoteRequestSchema,
  GenerateQuotePdfRequestSchema,
  GetQuoteDetailRequestSchema,
  UpdateQuoteRequestSchema,
  type QuoteEmailOverride,
} from '@/gen/cortado/v1/cortado_pb'
import { GeneratePortalLinkRequestSchema, type Inquiry } from '@/gen/cortado/v1/inquiries_pb'
import type { Quote } from '@/gen/cortado/v1/invoices_pb'
import { QuoteStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { useQuoteSend } from '@/hooks/use-quote-send'
import { usePortalLinkCopy } from '@/hooks/use-portal-link-copy'
import { useAcceptQuoteAsAdmin, useGeneratePortalLink } from '@/stores/leads.store'
import { useListItems } from '@/stores/organization.store'
import { useDeleteQuote, useDuplicateQuote, useGenerateQuotePdf, useUpdateQuote } from '@/stores/quotes.store'
import { useSystemSettings } from '@/stores/settings.store'
import { generateAndDownloadFile } from '@/lib/download-file'
import { getQuoteStatusLabel } from '@/lib/enum-utils'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { formatFullName, cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { QuoteSendDialog } from '@/components/quotes/quote-send-dialog'
import type { QuoteLineItem } from '@/components/quotes/quote-line-items-table'
import type { QuoteEditorFormValues, QuoteEditorTotals } from './quote-editor-form'
import { QuoteEditorPreviewSheet } from './quote-editor-preview-sheet'
import { useQuoteReadiness } from './use-quote-readiness'

const SENDABLE = new Set([
  QuoteStatusEnum.QUOTE_STATUS_DRAFT,
  QuoteStatusEnum.QUOTE_STATUS_SENT,
  QuoteStatusEnum.QUOTE_STATUS_VIEWED,
])
const ACCEPTABLE = new Set([QuoteStatusEnum.QUOTE_STATUS_SENT, QuoteStatusEnum.QUOTE_STATUS_VIEWED])

/** Why a quote in `status` can't be sent at all, independent of its content. */
function statusSendBlock(status: QuoteStatusEnum): string | undefined {
  if (SENDABLE.has(status)) return undefined
  switch (status) {
    case QuoteStatusEnum.QUOTE_STATUS_ACCEPTED:
      return 'This quote is already accepted.'
    case QuoteStatusEnum.QUOTE_STATUS_EXPIRED:
      return 'This quote has expired. Set it back to Draft or duplicate it to send again.'
    default:
      return `A ${getQuoteStatusLabel(status).toLowerCase()} quote can't be sent.`
  }
}

/** Readable copy for AcceptQuoteAsAdmin failures the backend reports by message. */
function acceptErrorDescription(error: unknown): string {
  const description = getErrorDescription(error)
  if (/already has a booking/i.test(description)) {
    return 'This lead is already booked from another quote. Cancel that booking before accepting this one.'
  }
  return description
}

export interface QuoteEditorActionsProps {
  /** The quote as last saved. */
  quote: Quote
  inquiry: Inquiry | undefined
  organizationId: string
  /** The editor's current values and totals, for the readiness checklist and the preview. */
  values: QuoteEditorFormValues
  totals: QuoteEditorTotals
  /** Every line on the quote, transportation included. */
  allLineItems: QuoteLineItem[]
  /** Makes sure the customer gets what's on screen: saves pending edits, or refuses while unsaved. */
  prepareToSend: () => Promise<boolean>
  onQuoteDuplicated?: (quote: Quote) => void
  onQuoteDeleted?: (quoteId: string) => void
  /** Fired after an action changes the quote server-side (the job's view of it should refresh). */
  onQuoteChanged?: () => void
  /**
   * `panel` (default) is the boxed bar inside the editor, with its own checklist.
   * `header` is a bare button row for a page header whose rail already shows the checklist.
   */
  variant?: 'panel' | 'header'
}

/**
 * Everything you can do with a saved quote from the pipeline editor: send or
 * resend (with the "before you send" checklist), change status, accept on the
 * customer's behalf, duplicate, PDF, portal link, customer preview, delete.
 * Notes, tasks, attachments and activity stay on the full quote page.
 */
export function QuoteEditorActions({
  quote,
  inquiry,
  organizationId,
  values,
  totals,
  allLineItems,
  prepareToSend,
  onQuoteDuplicated,
  onQuoteDeleted,
  onQuoteChanged,
  variant = 'panel',
}: QuoteEditorActionsProps) {
  const isHeader = variant === 'header'
  const navigate = useNavigate()
  const [isSendOpen, setIsSendOpen] = useState(false)
  const [isPreparingSend, setIsPreparingSend] = useState(false)
  const [isConfirmingAccept, setIsConfirmingAccept] = useState(false)
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  const status = quote.status
  const isDraft = status === QuoteStatusEnum.QUOTE_STATUS_DRAFT
  const isAccepted = status === QuoteStatusEnum.QUOTE_STATUS_ACCEPTED
  const contact = inquiry?.contact
  const recipientName = formatFullName(contact?.firstName, contact?.lastName, 'Unknown contact')
  const recipientEmail = contact?.email?.trim()

  const { data: convertedInvoice } = useQuery(
    getQuoteDetail,
    create(GetQuoteDetailRequestSchema, { id: quote.id }),
    { select: (data) => data.convertedInvoice },
  )
  const { checklist, stripeBlocksSend } = useQuoteReadiness({ organizationId, values, totals, allLineItems })
  const { items: catalogItems } = useListItems(organizationId)
  const { settings } = useSystemSettings()
  const { sendQuote, isSending } = useQuoteSend({ isDraft, onSuccess: onQuoteChanged })
  const { updateQuote, isLoading: isUpdatingStatus } = useUpdateQuote()
  const { acceptQuote, isLoading: isAccepting } = useAcceptQuoteAsAdmin()
  const { duplicateQuote, isLoading: isDuplicating } = useDuplicateQuote()
  const { deleteQuote, isLoading: isDeleting } = useDeleteQuote()
  const { generatePdf, isLoading: isGeneratingPdf } = useGenerateQuotePdf()
  const { generateLink } = useGeneratePortalLink()
  const { copyGenericLink, isCopying } = usePortalLinkCopy(
    (attributionMode) =>
      generateLink(create(GeneratePortalLinkRequestSchema, { inquiryId: quote.inquiryId, quoteId: quote.id, attributionMode })),
    (error) => {
      reportError(error, { feature: 'quote_editor_portal_link', kind: 'mutation' })
      toast.error('Failed to generate portal link', { description: getErrorDescription(error) })
    },
  )

  const sendBlock =
    statusSendBlock(status) ??
    (stripeBlocksSend ? 'Connect Stripe before sending a paid quote.' : undefined) ??
    checklist.find((item) => !item.satisfied && item.id !== 'stripe')?.label ??
    (recipientEmail ? undefined : 'Add an email address to the contact before sending.')

  const isValidUntilPast = quote.validUntil ? Number(quote.validUntil.seconds) * 1000 < Date.now() : false
  const acceptBlock = !ACCEPTABLE.has(status)
    ? 'Only a sent or viewed quote can be accepted.'
    : isValidUntilPast
      ? 'This quote is past its valid-until date.'
      : undefined
  const statusOptions = (settings?.quoteStatuses ?? []).filter(
    (option) =>
      option !== QuoteStatusEnum.QUOTE_STATUS_UNKNOWN &&
      // Accepted is only ever set by the customer (or "Accept as customer").
      option !== QuoteStatusEnum.QUOTE_STATUS_ACCEPTED &&
      option !== status,
  )

  async function handleSendClick() {
    setIsPreparingSend(true)
    try {
      if (await prepareToSend()) setIsSendOpen(true)
    } finally {
      setIsPreparingSend(false)
    }
  }

  async function handleSend(input: { additionalRecipientEmails: string[]; emailOverride: QuoteEmailOverride }) {
    return sendQuote({ quoteId: quote.id, ...input })
  }

  async function handleStatusChange(next: QuoteStatusEnum) {
    try {
      await updateQuote(
        create(UpdateQuoteRequestSchema, {
          id: quote.id,
          status: next,
          updateMask: create(FieldMaskSchema, { paths: ['status'] }),
        }),
      )
      toast.success(`Status updated to ${getQuoteStatusLabel(next)}`)
      onQuoteChanged?.()
    } catch (error) {
      reportError(error, { feature: 'quote_editor_status', kind: 'mutation' })
      toast.error('Failed to update status', { description: getErrorDescription(error) })
    }
  }

  async function handleAccept() {
    try {
      const response = await acceptQuote(quote.inquiryId, quote.id)
      setIsConfirmingAccept(false)
      onQuoteChanged?.()
      if (response.invoiceId) {
        const invoiceId = response.invoiceId
        toast.success('Quote accepted and invoice created', {
          action: { label: 'View invoice', onClick: () => navigate({ to: '/invoices/$id', params: { id: invoiceId } }) },
        })
      } else {
        toast.success('Quote accepted')
      }
    } catch (error) {
      reportError(error, { feature: 'quote_editor_accept', kind: 'mutation' })
      toast.error('Failed to accept quote', { description: acceptErrorDescription(error) })
    }
  }

  async function handleDuplicate() {
    try {
      const response = await duplicateQuote(create(DuplicateQuoteRequestSchema, { id: quote.id }))
      if (response.quote) {
        toast.success(`Duplicated as Quote #${response.quote.quoteNumber}`)
        onQuoteDuplicated?.(response.quote)
        onQuoteChanged?.()
      }
    } catch (error) {
      reportError(error, { feature: 'quote_editor_duplicate', kind: 'mutation' })
      toast.error('Failed to duplicate quote', { description: getErrorDescription(error) })
    }
  }

  async function handleDownloadPdf() {
    try {
      await generateAndDownloadFile(() => generatePdf(create(GenerateQuotePdfRequestSchema, { quoteId: quote.id })), 'Quote.pdf')
    } catch (error) {
      reportError(error, { feature: 'quote_editor_pdf', kind: 'mutation' })
      toast.error('Failed to download quote PDF', { description: getErrorDescription(error) })
    }
  }

  async function handleDelete() {
    try {
      await deleteQuote(quote.id)
      setIsConfirmingDelete(false)
      toast.success(`Quote #${quote.quoteNumber} deleted`)
      onQuoteDeleted?.(quote.id)
      onQuoteChanged?.()
    } catch (error) {
      reportError(error, { feature: 'quote_editor_delete', kind: 'mutation' })
      toast.error('Failed to delete quote', { description: getErrorDescription(error) })
    }
  }

  const sentAt = quote.sentAt ? new Date(Number(quote.sentAt.seconds) * 1000) : undefined
  const isOut = status === QuoteStatusEnum.QUOTE_STATUS_SENT || status === QuoteStatusEnum.QUOTE_STATUS_VIEWED

  return (
    <div className={isHeader ? 'space-y-2' : 'space-y-3 rounded-xl border bg-muted/20 p-3'}>
      {convertedInvoice && (
        <p className="rounded-md border border-status-warning-border bg-status-warning-bg px-3 py-2 text-xs text-status-warning-fg">
          Converted to Invoice #{convertedInvoice.invoiceNumber}. The client pays from that invoice, so pricing and payment
          changes here won&apos;t reach them.{' '}
          <Link to="/invoices/$id" params={{ id: convertedInvoice.id }} className="font-medium underline underline-offset-2">
            Open Invoice #{convertedInvoice.invoiceNumber}
          </Link>
        </p>
      )}

      {!isHeader && SENDABLE.has(status) && (
        <ul aria-label="Before you send" className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {checklist.map((item) => (
            <li key={item.id} className={cn('flex items-center gap-1', item.satisfied ? 'text-muted-foreground' : 'text-status-warning-fg')}>
              {item.satisfied ? <Check className="size-3.5" aria-hidden /> : <Circle className="size-3.5" aria-hidden />}
              <span>{item.label}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          onClick={() => void handleSendClick()}
          disabled={Boolean(sendBlock) || isSending || isPreparingSend}
          title={sendBlock}
        >
          {isSending || isPreparingSend ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
          {isDraft ? 'Send quote' : 'Resend quote'}
        </Button>

        <Button type="button" variant="outline" size="sm" onClick={() => setIsPreviewOpen(true)}>
          <Eye className="size-4" />
          Preview quote
        </Button>

        {!isAccepted && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" size="sm" disabled={isUpdatingStatus} aria-label={`Change status (${getQuoteStatusLabel(status)})`}>
                {getQuoteStatusLabel(status)}
                <ChevronDown className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuLabel>Set status</DropdownMenuLabel>
              {statusOptions.map((option) => (
                <DropdownMenuItem key={option} onSelect={() => void handleStatusChange(option)}>
                  {getQuoteStatusLabel(option)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="icon-sm" aria-label="More quote actions">
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => void copyGenericLink()} disabled={isCopying}>Copy portal link</DropdownMenuItem>
            {!isDraft && (
              <DropdownMenuItem onSelect={() => void handleDownloadPdf()} disabled={isGeneratingPdf}>Download PDF</DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => void handleDuplicate()} disabled={isDuplicating}>
              {isHeader ? 'Duplicate as alternative' : 'Duplicate'}
            </DropdownMenuItem>
            {!isAccepted && (
              <DropdownMenuItem onSelect={() => setIsConfirmingAccept(true)} disabled={Boolean(acceptBlock)} title={acceptBlock}>
                Accept as customer…
              </DropdownMenuItem>
            )}
            {!isAccepted && !convertedInvoice && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onSelect={() => setIsConfirmingDelete(true)}>
                  Delete quote…
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {!isHeader && (
          <Link
            to="/quotes/$id"
            params={{ id: quote.id }}
            className="ml-auto text-xs text-muted-foreground hover:text-foreground"
            title="Notes, tasks, attachments and activity"
          >
            View full quote →
          </Link>
        )}
      </div>
      {isHeader && isOut && sentAt && (
        <p className="text-xs text-muted-foreground" role="note">
          Sent{recipientEmail ? ` to ${recipientEmail}` : ''} {formatDistanceToNow(sentAt, { addSuffix: true })}. Edits
          create a revision; resend to update the customer.
        </p>
      )}
      {sendBlock && (
        <p className="text-xs text-muted-foreground" role="note">
          Can&apos;t send: {sendBlock}
        </p>
      )}

      <QuoteSendDialog
        key={quote.id}
        open={isSendOpen}
        onOpenChange={setIsSendOpen}
        organizationId={organizationId}
        quoteId={quote.id}
        isDraft={isDraft}
        recipientName={recipientName}
        recipientEmail={recipientEmail}
        isSending={isSending}
        stripeBlocksSend={stripeBlocksSend}
        onSend={handleSend}
      />

      <QuoteEditorPreviewSheet
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        recipientFirstName={contact?.firstName?.trim() || recipientName}
        values={values}
        lineItems={allLineItems}
        catalogItems={catalogItems}
        totals={totals}
      />

      <AlertDialog open={isConfirmingAccept} onOpenChange={(open) => !isAccepting && setIsConfirmingAccept(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Accept Quote #{quote.quoteNumber} for the customer?</AlertDialogTitle>
            <AlertDialogDescription>
              This books the job and creates its invoice, as if {recipientName} accepted it in the portal. It can&apos;t be undone here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isAccepting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isAccepting}
              onClick={(event) => {
                event.preventDefault()
                void handleAccept()
              }}
            >
              {isAccepting ? 'Accepting…' : 'Accept quote'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isConfirmingDelete} onOpenChange={(open) => !isDeleting && setIsConfirmingDelete(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Quote #{quote.quoteNumber}?</AlertDialogTitle>
            <AlertDialogDescription>The quote and its revisions are removed. This can&apos;t be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isDeleting}
              variant="destructive"
              onClick={(event) => {
                event.preventDefault()
                void handleDelete()
              }}
            >
              {isDeleting ? 'Deleting…' : 'Delete quote'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
